import { useEffect, useRef } from 'react';

import { useAuth } from '@clerk/react';
import { Notification, WebSocketEntityType } from '@this+that/models';
import log from 'loglevel';

import { useGetUnreadNotifications } from '../../../services/notification.service';
import { useNotificationsStore } from '../../../store';
import { NOTIFICATIONS_PAGINATION_LIMIT } from '../../../utils/constants';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

/**
 * Hook to synchronize Notifications WebSocket events with the store
 *
 * Subscribes to:
 * - INSERT: Routes notification to unread or read array based on wasRead
 * - MODIFY: Updates notification; if wasRead transitions false→true, moves it from unread to read array
 * - REMOVE: Removes notification by ID
 * - RECONNECT_AFTER_LONG_PAUSE: Refetches unread notifications and invalidates the read tab cache
 */
export function useNotificationsSync() {
  const { isLoaded, isSignedIn } = useAuth();
  const { subscribe } = useWebSocket();
  const addNotification = useNotificationsStore((s) => s.addNotification);
  const updateNotification = useNotificationsStore((s) => s.updateNotification);
  const removeNotification = useNotificationsStore((s) => s.removeNotification);
  const setNotifications = useNotificationsStore((s) => s.setNotifications);
  const setHasLoadedReadTab = useNotificationsStore(
    (s) => s.setHasLoadedReadTab,
  );
  const setIsInitialLoading = useNotificationsStore(
    (s) => s.setIsInitialLoading,
  );

  const { getUnreadNotifications } = useGetUnreadNotifications();

  const getUnreadNotificationsRef = useRef(getUnreadNotifications);
  getUnreadNotificationsRef.current = getUnreadNotifications;
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }

    const fetchInitial = async () => {
      try {
        const result = await getUnreadNotificationsRef.current(
          NOTIFICATIONS_PAGINATION_LIMIT,
        );
        result?.notifications?.forEach((n) => addNotificationRef.current(n));
      } catch (error) {
        log.error(
          '[NotificationsSync] Failed to fetch initial notifications:',
          error,
        );
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchInitial();
  }, [isLoaded, isSignedIn, setIsInitialLoading]);

  useEffect(() => {
    const unsubscribeReconnect = subscribe(
      WebSocketEntityType.Notification,
      WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
      async () => {
        log.info(
          '[NotificationsSync] Reconnected after long pause, fetching notifications',
        );

        try {
          const result = await getUnreadNotificationsRef.current(
            NOTIFICATIONS_PAGINATION_LIMIT,
          );
          if (result?.notifications) {
            setNotifications(result.notifications);
            setHasLoadedReadTab(false);
          }
        } catch (error) {
          log.error(
            '[NotificationsSync] Failed to fetch notifications:',
            error,
          );
        }
      },
    );

    const unsubscribeInsert = subscribe<Notification>(
      WebSocketEntityType.Notification,
      WebSocketEventType.INSERT,
      ({ item }) => {
        if (!item) {
          return;
        }
        addNotification(item);
      },
    );

    const unsubscribeModify = subscribe<Notification>(
      WebSocketEntityType.Notification,
      WebSocketEventType.MODIFY,
      ({ item }) => {
        if (!item) {
          return;
        }
        updateNotification(item);
      },
    );

    const unsubscribeRemove = subscribe<Notification>(
      WebSocketEntityType.Notification,
      WebSocketEventType.REMOVE,
      ({ item }) => {
        if (!item?.id) {
          return;
        }
        removeNotification(item.id);
      },
    );

    return () => {
      unsubscribeReconnect();
      unsubscribeInsert();
      unsubscribeModify();
      unsubscribeRemove();
    };
  }, [
    subscribe,
    addNotification,
    updateNotification,
    removeNotification,
    setNotifications,
    setHasLoadedReadTab,
  ]);
}
